import { Button as ButtonV2, Flex } from '@revenuemarket/cds';
import { colors } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { useFullSpinnerStore } from '@revenuemarket/react';
import { Toast } from '@revenuemarket/ui';
import { Button, ButtonProps, Modal, Typography } from 'antd';
import { useRouter } from 'next/router';

import { addClobeMember, getIssueConnectStatus, signupBarobill } from '~apis/tax-invoice';
import { useCompanyMember } from '~hooks/queries/companies/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { useUserId } from '~hooks/useUserId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import ConfirmCompnayInfoModal from './ConfirmCompanyInfoModal';
import ConnectBaroBillModal from './ConnectBaroBillModal';
import ConnectSuccessModal from './ConnectSuccessModal';
import LoginInfoConfirmModal from './LoginInfoConfirmModal';
import RegisterBaroBillModal from './RegisterBaroBillModal';

interface Props extends ButtonProps {
  link?: string;
  skipLink?: boolean;
  onIssueInvoice?: () => void;
  issueType?: string;
  isNewButton?: boolean;
  from?: string;
  trigger?: React.ReactNode;
}

const IssueButton: React.FC<Props> = ({
  link,
  skipLink = false,
  onIssueInvoice,
  from,
  trigger,
  ...props
}) => {
  const router = useRouter();
  const [modal, contextHolder] = Modal.useModal();
  const { companyId } = useCompanyId();
  const userId = useUserId();
  const { showFullSpinner, hideFullSpinner } = useFullSpinnerStore();

  const { data: companyUserDetail } = useCompanyMember({ companyId, memberId: userId });

  const companyUserDetailData = companyUserDetail?.Data?.data;
  const onCheckCert = async ({ url }: { url: string }) => {
    if (window.navigator.platform.toLocaleLowerCase().includes('mac')) {
      modal.warning({
        cancelButtonProps: {
          style: {
            display: 'none',
          },
        },
        title: '전자세금용 공동인증서 등록은 Window PC를 이용해주세요',
        content:
          '공동인증서 등록 이후에는 Mac에서도 전자(세금)계산서를 발급할 수 있어요. 최초 1회에 한해 Window PC에서 공동인증서를 등록해주세요.',
      });
      return;
    }

    const statusCheck = async () => {
      const response = await getIssueConnectStatus({ companyId });
      const status = response.Data?.data?.connectStatus;
      if (status === 'CONNECTED') {
        router.push('/clobe/tax-invoice/issue');
        hideFullSpinner();
      } else {
        Toast.error('공동인증서 연동이 완료되지 않았습니다.');
        hideFullSpinner();
      }
    };
    window.open(url, '_blank', 'width=800, height=600');
    showFullSpinner(
      <Flex direction="column" gap={20}>
        <Typography.Text style={{ color: colors.grayscaleWhite, fontSize: 14 }}>
          팝업 창에서 공동인증서 등록을 완료하신 뒤에 <br />
          아래 공동인증서 등록 완료 버튼을 클릭해주세요
        </Typography.Text>
        <Button type="primary" onClick={statusCheck}>
          공동인증서 등록 완료
        </Button>
        <Button
          type="link"
          onClick={() => {
            window.open(url, '_blank', 'width=800, height=600');
          }}
        >
          팝업 창이 안 열리나요?
        </Button>
      </Flex>,
    );
  };

  const onConnect = () => {
    overlays.open(({ isOpen, onClose }) => (
      <ConnectBaroBillModal
        open={isOpen}
        onCancel={onClose}
        onOk={() => {
          onClose();
          overlays.open(({ isOpen, onClose }) => (
            <ConfirmCompnayInfoModal
              open={isOpen}
              onCancel={onClose}
              onComplete={async () => {
                onClose();
                overlays.open(({ isOpen, onClose }) => (
                  <LoginInfoConfirmModal
                    open={isOpen}
                    onCancel={onClose}
                    onComplete={async (barobillId: string) => {
                      onClose();
                      await addClobeMember({ companyId, memberId: barobillId });
                      mixpanel.track(MIXPANEL_EVENT.TAX_INVOICE.ADD_CLOBE_MEMBER_TO_BAROBILL);
                      overlays.open(({ isOpen, onClose }) => (
                        <ConnectSuccessModal
                          open={isOpen}
                          onCancel={onClose}
                          emails={[companyUserDetailData?.email as string]}
                          onOk={async () => {
                            onClose();
                          }}
                        />
                      ));
                    }}
                  />
                ));
              }}
            />
          ));
        }}
      />
    ));
  };

  const onRegister = () => {
    overlays.open(({ isOpen, onClose }) => (
      <ConnectBaroBillModal
        open={isOpen}
        onCancel={onClose}
        onOk={async () => {
          onClose();
          overlays.open(({ isOpen, onClose }) => (
            <ConfirmCompnayInfoModal
              open={isOpen}
              onCancel={onClose}
              onComplete={() => {
                onClose();
                overlays.open(({ isOpen, onClose }) => (
                  <RegisterBaroBillModal
                    open={isOpen}
                    onCancel={onClose}
                    onFinish={async (values) => {
                      const response = await signupBarobill({ companyId, request: values });
                      const text = '바로빌에 회원가입하는 중입니다';
                      showFullSpinner(text);
                      if (response.Status === ResponseStatus.SUCCESS) {
                        const responseForIssue = await getIssueConnectStatus({ companyId });
                        const url = responseForIssue.Data?.data?.additionalUrl ?? '';
                        setTimeout(() => {
                          Toast.success('바로빌 회원가입이 완료되었습니다.');
                          hideFullSpinner();
                          onCheckCert({ url });
                        }, 1000);
                        onClose();
                      } else {
                        Toast.error(response.ErrorMsg || '바로빌 회원가입에 실패했습니다.');
                        hideFullSpinner();
                      }
                    }}
                  />
                ));
              }}
            />
          ));
        }}
      />
    ));
  };

  const onWaitingManualAddMember = () => {
    overlays.open(({ isOpen, onClose }) => (
      <ConnectSuccessModal
        open={isOpen}
        onCancel={onClose}
        emails={[companyUserDetailData?.email as string]}
        onOk={onClose}
      />
    ));
  };

  const onIssue = async () => {
    mixpanel.track(MIXPANEL_EVENT.TAX_INVOICE.ISSUE_TRIGER, {
      issueType: props?.issueType ?? 'default',
      from,
    });
    const response = await getIssueConnectStatus({ companyId });

    if (response.Status === ResponseStatus.FAILURE) {
      Toast.error(response.ErrorMsg || '세금 계산서 발급 정보를 가져오는데 실패했습니다.');
      return;
    }

    const status = response.Data?.data?.connectStatus;

    const url = response.Data?.data?.additionalUrl ?? '';
    switch (status) {
      case 'CONNECTED':
        onIssueInvoice?.();
        !skipLink && window.open(link ?? '/clobe/tax-invoice/issue', '_blank');
        return;
      case 'NEED_CERTIFICATION':
        onCheckCert({ url });
        return;
      case 'NEED_CONNECT':
        onRegister();
        return;
      case 'NEED_ADD_MEMBER_CORP':
        onConnect();
        return;
      case 'WAITING_ADD_MEMBER_CORP':
        onWaitingManualAddMember();
        return;
    }
  };

  return (
    <>
      {contextHolder}
      {trigger ? (
        <div onClick={onIssue}>{trigger}</div>
      ) : props.isNewButton ? (
        <ButtonV2
          buttonType="primary"
          buttonSize="small"
          border
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
          onClick={onIssue}
        >
          {props.children}
        </ButtonV2>
      ) : (
        <Button {...props} onClick={onIssue}></Button>
      )}
    </>
  );
};

export default IssueButton;
